// CarbonBridgeTestCasesDoc.tsx
import React from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableFooter,
} from "@mui/material";

const CarbonBridgeTestCasesDoc: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            CarbonBridge / CarbonChain – Test Case Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test-case skeletons for functional, non-functional, and
            compliance/AI scenarios. You can copy these into your test
            management tool or extend them directly in code.
          </Typography>
        </Box>

        {/* TABLE 1 – Functional / Flow Tests – Standard Scenario */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            1. Functional & Flow Tests – Standard Scenario
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Covers single-SME, multi-device deployment on one private chain with
            mint, price, and retire flows. These map to scenario IDs TS-001 –
            TS-008.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Test Case ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Preconditions</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Expected Result</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Related Scenario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* TC-001 */}
                <TableRow>
                  <TableCell>TC-001</TableCell>
                  <TableCell>Mint credit – happy path</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Device DEV_A is whitelisted.</li>
                      <li>Valid kWh readings stored for last 24h.</li>
                      <li>Emission factor configured for region.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Call Policy Gateway <code>/policy/check/mint</code> with DEV_A, kWh, emission factor, and requested credits.</li>
                      <li>Verify Extractor output (normalized request).</li>
                      <li>Verify Enforcer decision is <code>approve</code>.</li>
                      <li>Trigger mint transaction through orchestrator.</li>
                      <li>Query chain to confirm NFT credit is minted.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Enforcer returns <code>approve</code> with high compliance score.</li>
                      <li>Exactly one credit per tCO₂e is minted.</li>
                      <li>Credit metadata links to DEV_A and measurement interval.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>TS-001</TableCell>
                </TableRow>

                {/* TC-002 */}
                <TableRow>
                  <TableCell>TC-002</TableCell>
                  <TableCell>Mint credit – over-claim protection</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Device DEV_A whitelisted.</li>
                      <li>Stored kWh and emission factor yield 3.6 tCO₂e.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Call <code>/policy/check/mint</code> with requested_credits = 5.</li>
                      <li>Inspect Enforcer response.</li>
                      <li>Attempt to trigger mint despite decision (as a negative test).</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Enforcer calculates max allowable credits (3) and rejects or flags request.</li>
                      <li>Policy Gateway does not trigger any mint on-chain.</li>
                      <li>No new credits exist after test.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>TS-002</TableCell>
                </TableRow>

                {/* TC-003 */}
                <TableRow>
                  <TableCell>TC-003</TableCell>
                  <TableCell>Mint credit – unknown device</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Device DEV_X not on whitelisted registry.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Call <code>/policy/check/mint</code> using DEV_X and valid kWh.</li>
                      <li>Observe Enforcer decision and missing_data/violated_rules.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Enforcer returns <code>needs_more_data</code> or <code>reject</code> referencing device whitelist rule.</li>
                      <li>Mint is blocked until device is whitelisted.</li>
                    </ul>
                  </TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>TS-003</TableCell>
                </TableRow>

                {/* TC-004 */}
                <TableRow>
                  <TableCell>TC-004</TableCell>
                  <TableCell>Price credits – benchmark sanity check</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>At least one minted credit exists.</li>
                      <li>External carbon price benchmark configured.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Propose a price/credit via <code>/policy/check/price</code>.</li>
                      <li>Ensure payload includes benchmark reference.</li>
                      <li>Inspect Enforcer pricing reasoning and decision.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Enforcer accepts prices within configured bounds of benchmark × modifiers.</li>
                      <li>Clearly states pricing_basis in normalised_facts.</li>
                    </ul>
                  </TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>TS-004</TableCell>
                </TableRow>

                {/* TC-005 */}
                <TableRow>
                  <TableCell>TC-005</TableCell>
                  <TableCell>Retire credit – owner path</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>User U1 owns credit ID CRED_1.</li>
                      <li>Credit is in active state (not retired).</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Call <code>/policy/check/retire</code> as U1 for CRED_1.</li>
                      <li>Confirm Enforcer approves retirement.</li>
                      <li>Execute on-chain retire function.</li>
                      <li>Query chain and DB for updated status.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Credit status transitions to retired on-chain and off-chain.</li>
                      <li>Retirement reason, timestamp, and actor recorded in logs.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>TS-005</TableCell>
                </TableRow>

                {/* TC-006 */}
                <TableRow>
                  <TableCell>TC-006</TableCell>
                  <TableCell>Retire credit – non-owner blocked</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Credit ID CRED_1 owned by U1.</li>
                      <li>User U2 has no ownership or special permissions.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>As U2, call <code>/policy/check/retire</code> for CRED_1.</li>
                      <li>Attempt to execute retire on-chain (negative test).</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Enforcer rejects based on role/ownership checks.</li>
                      <li>On-chain transaction reverts or is never called.</li>
                      <li>Credit remains active and unchanged.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>TS-006</TableCell>
                </TableRow>

                {/* TC-007 */}
                <TableRow>
                  <TableCell>TC-007</TableCell>
                  <TableCell>MQTT ingestion – end-to-end</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>MQTT broker running.</li>
                      <li>Device DEV_A configured to publish to relevant topic.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Publish sample readings from DEV_A.</li>
                      <li>Verify broker forwards to ingestion service.</li>
                      <li>Confirm readings persisted in measurement DB.</li>
                      <li>Mint credit using same readings.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Readings appear in DB with correct device ID and timestamps.</li>
                      <li>Minting can use these readings as input, not ad-hoc data.</li>
                    </ul>
                  </TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>TS-007</TableCell>
                </TableRow>

                {/* TC-008 */}
                <TableRow>
                  <TableCell>TC-008</TableCell>
                  <TableCell>Auth & roles – issuer vs buyer</TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Issuer role and buyer role configured.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Log in as issuer, attempt to mint credit.</li>
                      <li>Log in as buyer, attempt same mint operation.</li>
                      <li>Observe gateway/Enforcer behaviour and on-chain results.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Issuer can complete mint (subject to other rules).</li>
                      <li>Buyer cannot mint; Enforcer or gateway denies action.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                  <TableCell>TS-008</TableCell>
                </TableRow>
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="caption" color="text.secondary">
                      Note: You can add columns for Test Data, Result, and
                      Defect ID when exporting into a test management system.
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>

        {/* TABLE 2 – Non-functional Tests */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            2. Non-Functional Test Templates (Performance, Reliability, Security)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Non-functional test cases aligned to ISO 25010 quality attributes
            and OWASP ASVS security verification.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Test Case ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Area</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Objective</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Setup</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Execution</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Expected Result</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* NFT-001 */}
                <TableRow>
                  <TableCell>NFT-001</TableCell>
                  <TableCell>Performance – Mint throughput</TableCell>
                  <TableCell>
                    Measure system behaviour under sustained mint requests.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Load test tool configured (k6, JMeter, etc.).</li>
                      <li>Realistic seed data and whitelisted devices.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    Run mint flow (policy check → LLMs → on-chain) at increasing
                    request rates (e.g., 5, 20, 50 req/s).
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Throughput and latency remain within agreed SLOs.</li>
                      <li>No error spikes or memory leaks observed.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* NFT-002 */}
                <TableRow>
                  <TableCell>NFT-002</TableCell>
                  <TableCell>Reliability – Service restart</TableCell>
                  <TableCell>
                    Assess resilience and state consistency across restarts.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Monitoring and logs enabled.</li>
                      <li>Background mint/retire operations running.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    Restart Policy Gateway, LLM proxy, and one chain node while
                    mint/retire requests are active.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>No duplicate or missing credits.</li>
                      <li>In-flight operations either fully succeed or cleanly fail (no half-applied state).</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* NFT-003 */}
                <TableRow>
                  <TableCell>NFT-003</TableCell>
                  <TableCell>Security – Auth & input validation</TableCell>
                  <TableCell>
                    Validate authentication, authorisation, and input validation
                    per ASVS.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Test accounts with different roles.</li>
                      <li>Security test proxy or tooling available.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    Attempt to invoke policy and execution endpoints with:
                    missing tokens, invalid roles, oversized payloads, and
                    injection-like input.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Unauthorised requests are rejected with safe error messages.</li>
                      <li>No injection or deserialisation issues observed.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* NFT-004 */}
                <TableRow>
                  <TableCell>NFT-004</TableCell>
                  <TableCell>Observability – Logs & traces</TableCell>
                  <TableCell>
                    Confirm sufficient logs/metrics for incident analysis.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Centralised logging/metrics system set up.</li>
                    </ul>
                  </TableCell>
                  <TableCell>
                    Execute common flows (mint, retire, bridge) and inspect logs
                    and traces.
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>All critical actions have correlation IDs.</li>
                      <li>Policy decisions and on-chain tx hashes can be linked.</li>
                    </ul>
                  </TableCell>
                  <TableCell>Medium</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* TABLE 3 – Compliance / AI / Blockchain tests (Complex Scenario) */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            3. Compliance, AI-Risk & Blockchain Integrity Tests – Complex Scenario
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            These test cases target multi-org, multi-region, multi-chain
            deployment with heavy regulatory expectations and AI-governed policy
            logic.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Test Case ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Focus</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Objective</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Expected Result</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* CCT-001 */}
                <TableRow>
                  <TableCell>CCT-001</TableCell>
                  <TableCell>AI – Robustness & over-claim</TableCell>
                  <TableCell>
                    Ensure Enforcer never approves requests that violate 1
                    credit = 1 tCO₂e, even under adversarial inputs.
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Craft payloads that attempt to overstate kWh or emission factor.</li>
                      <li>Inject misleading descriptions in free-text fields.</li>
                      <li>Submit through Policy Gateway and inspect Enforcer results.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Every over-claim attempt leads to <code>reject</code> or <code>needs_more_data</code>.</li>
                      <li>Reasoning explicitly cites the violated energy/CO₂ rule.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* CCT-002 */}
                <TableRow>
                  <TableCell>CCT-002</TableCell>
                  <TableCell>AI – Consistency & repeatability</TableCell>
                  <TableCell>
                    Confirm that identical inputs yield stable decisions over
                    time and across LLM restarts.
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Select a representative set of mint/retire/price requests.</li>
                      <li>Replay each request multiple times over several hours/days.</li>
                      <li>Record decisions and compliance_score each time.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Decisions remain the same or within pre-agreed tolerance range.</li>
                      <li>No random approvals/rejections with identical inputs.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* CCT-003 */}
                <TableRow>
                  <TableCell>CCT-003</TableCell>
                  <TableCell>Blockchain – Freeze–burn minting</TableCell>
                  <TableCell>
                    Verify that tokens used to mint credits are properly
                    frozen/locked and then burned or kept permanently locked.
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Mint ERGON/CARBON tokens representing energy.</li>
                      <li>Use them in a mint flow that should freeze/burn them.</li>
                      <li>Inspect token balances and smart contract state after mint.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Tokens used for mint are no longer transferable or spendable.</li>
                      <li>Credit NFT metadata references burned/frozen token IDs or batch.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* CCT-004 */}
                <TableRow>
                  <TableCell>CCT-004</TableCell>
                  <TableCell>Cross-chain – No double existence</TableCell>
                  <TableCell>
                    Ensure that bridging does not result in live, spendable
                    assets on both source and target chains for the same
                    climate benefit.
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Lock tokens/credits on source chain via bridge contract.</li>
                      <li>Mint representation on target chain.</li>
                      <li>Attempt to unlock or transfer on source while target copy exists.</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Source assets remain locked while target representation is active.</li>
                      <li>Any attempt to double-spend is rejected at contract level.</li>
                    </ul>
                  </TableCell>
                  <TableCell>High</TableCell>
                </TableRow>

                {/* CCT-005 */}
                <TableRow>
                  <TableCell>CCT-005</TableCell>
                  <TableCell>Audit – End-to-end traceability</TableCell>
                  <TableCell>
                    Demonstrate that a random credit can be fully traced from
                    measurement → token → credit → retirement.
                  </TableCell>
                  <TableCell>
                    <ol style={{ paddingLeft: 16, margin: 0 }}>
                      <li>Pick a random retired credit.</li>
                      <li>Trace back to on-chain mint tx, off-chain policy decision, and device measurement logs.</li>
                      <li>Export this as an evidence pack (logs/CSV/PDF).</li>
                    </ol>
                  </TableCell>
                  <TableCell>
                    <ul style={{ paddingLeft: 16, margin: 0 }}>
                      <li>All steps are provable with timestamps, IDs, and hashes.</li>
                      <li>Evidence pack can be reviewed by an external auditor without missing links.</li>
                    </ul>
                  </TableCell>
                  <TableCell>Medium</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Container>
  );
};

export default CarbonBridgeTestCasesDoc;
